/** IANA timezone string used as fallback when no explicit timezone is provided. */
export const DEFAULT_TIMEZONE = 'America/Los_Angeles';
// Any date works — only the part TYPE ('month'/'day'/'year') matters for ordering.
const REFERENCE_DATE = new Date(2013, 4, 1); // HoneyBook founded
// medium uses unicode tokens (date-fns / react-datepicker); no moment variant needed.
const TOKENS = {
    moment: { month: 'MM', day: 'DD', year: 'YYYY' },
    unicode: { month: 'MM', day: 'dd', year: 'yyyy' },
    medium: { month: 'MMM', day: 'd', year: 'yyyy' }
};
const INTL_OPTIONS = {
    moment: { year: 'numeric', month: '2-digit', day: '2-digit' },
    unicode: { year: 'numeric', month: '2-digit', day: '2-digit' },
    medium: { year: 'numeric', month: 'short', day: 'numeric' }
};
// Static map — Intl.Locale.getWeekInfo() has no Firefox support as of writing.
// Keyed by country: firstDayOfWeek is a regional convention, not language-specific
// (e.g. a future fr-CA shares the same value as en-CA).
// Verify this map whenever adding a new locale — it is a deliberate product decision.
// 0 = Sunday, 1 = Monday
const FIRST_DAY_OF_WEEK = {
    US: 0,
    GB: 1,
    AU: 1,
    CA: 0
};
function formatDateParts(locale, type) {
    const parts = new Intl.DateTimeFormat(locale, INTL_OPTIONS[type]).formatToParts(REFERENCE_DATE);
    return parts.map(p => TOKENS[type][p.type] ?? p.value).join('');
}
/**
 * Returns a locale-aware date format string.
 *
 * - `'moment'` (default) — moment/dayjs tokens (`MM/DD/YYYY`). For dayjs, Moment.js, and placeholder text.
 * - `'unicode'` — UTS#35 tokens (`MM/dd/yyyy`). For date-fns, react-datepicker, and other UTS#35-aware libraries.
 * - `'medium'` — abbreviated-month unicode tokens (`MMM d, yyyy` / `d MMM yyyy`). Locale-aware verbose display for date inputs.
 *
 * @example
 * getDateFormat('en-US')                      // → 'MM/DD/YYYY'
 * getDateFormat('en-GB')                      // → 'DD/MM/YYYY'
 * getDateFormat('en-US', { type: 'unicode' }) // → 'MM/dd/yyyy'
 * getDateFormat('en-GB', { type: 'unicode' }) // → 'dd/MM/yyyy'
 * getDateFormat('en-US', { type: 'medium' })  // → 'MMM d, yyyy'   (renders Apr 12, 2025)
 * getDateFormat('en-GB', { type: 'medium' })  // → 'd MMM yyyy'    (renders 12 Apr 2025)
 */
export function getDateFormat(locale, { type = 'moment' } = {}) {
    return formatDateParts(locale, type);
}
/**
 * Returns the first day of the week for a locale. 0 = Sunday, 1 = Monday.
 * Based on a country-keyed static map (week start is a regional convention, not language-specific).
 *
 * @example
 * getFirstDayOfWeek('en-GB') // → 1 (Monday)
 * getFirstDayOfWeek('en-US') // → 0 (Sunday)
 */
export function getFirstDayOfWeek(locale) {
    return FIRST_DAY_OF_WEEK[locale.split('-')[1]] ?? 0;
}
const ISO_DATE_ONLY_REGEX = /^\d{4}-\d{2}-\d{2}$/;
/**
 * Returns true when the string is an ISO date-only value (`YYYY-MM-DD`).
 * Use this before parsing to decide whether to treat the value as a local
 * calendar day (no timezone conversion) rather than a UTC instant.
 *
 * @example
 * isISODateOnly('2026-05-17')          // → true
 * isISODateOnly('2026-05-17T12:00:00Z') // → false
 * isISODateOnly('05/17/2026')           // → false
 */
export function isISODateOnly(value) {
    return ISO_DATE_ONLY_REGEX.test(value);
}
/**
 * Normalises any supported date input to a JS Date.
 * Exported for use by sibling modules (e.g. timezone.ts) — not part of the public API surface.
 */
export function toDate(value) {
    if (value instanceof Date) {
        return value;
    }
    if (typeof value === 'number') {
        return new Date(value);
    }
    if (isISODateOnly(value)) {
        // ISO date-only string (YYYY-MM-DD): parse as LOCAL midnight, not UTC midnight.
        // new Date('2026-05-17') → UTC midnight → shifts the day in UTC- timezones.
        // Manually constructing avoids that shift for pure calendar-day fields.
        const [y, m, d] = value.split('-').map(Number);
        return new Date(y, m - 1, d);
    }
    return new Date(value);
}
/**
 * Builds the `Intl.DateTimeFormat` options for a given display format.
 *
 * Single source of truth for every `DateFormatType` — shared by `formatDateByLocale`
 * (no timezone) and `formatDateByTimezone` (sibling module). Adding a format here
 * makes it available to both; keeping the mapping in one place avoids the split
 * where one formatter silently falls through to numeric for an unhandled format.
 *
 * Exported for use by sibling modules (e.g. timezone.ts) — not part of the public API surface.
 */
export function intlDisplayOptions(format, timezone) {
    if (format === 'time') {
        const base = {
            hour: 'numeric',
            minute: '2-digit',
            // Explicit and deliberate — omitting this lets some browsers (e.g.
            // Chrome) fall back to the OS's 12h/24h clock preference instead of
            // the locale's own default, silently dropping AM/PM even for 'en-US'.
            hour12: true
        };
        if (timezone) {
            base.timeZone = timezone;
        }
        return base;
    }
    if (format === 'monthDay') {
        return {
            month: 'long',
            day: 'numeric',
            ...(timezone && { timeZone: timezone })
        };
    }
    const verbose = format === 'medium' || format === 'mediumDatetime' || format === 'long';
    const base = {
        year: 'numeric',
        month: verbose ? 'short' : '2-digit',
        day: verbose ? 'numeric' : '2-digit'
    };
    if (format === 'datetime') {
        base.hour = '2-digit';
        base.minute = '2-digit';
    }
    if (format === 'mediumDatetime') {
        base.hour = 'numeric';
        base.minute = '2-digit';
        base.hour12 = true;
    }
    if (format === 'long') {
        base.weekday = 'short';
        base.hour = 'numeric';
        base.minute = '2-digit';
    }
    if (timezone) {
        base.timeZone = timezone;
    }
    return base;
}
/**
 * Formats a date with a normalized day-period token — used only for the
 * `'time'` format. Locales render the AM/PM token inconsistently by default
 * (`en-GB`/`en-AU` → "pm", `en-CA` → "p.m."); this normalizes it to
 * uppercase with periods stripped so output always matches "AM"/"PM". Also
 * normalizes the separator before it: `Intl` inserts U+202F (narrow no-break
 * space), not a regular space, between the time and the day period —
 * invisible in most renderers but a real character difference.
 *
 * Exported for use by sibling modules (e.g. timezone.ts) — not part of the public API surface.
 */
export function formatTimeWithNormalizedDayPeriod(formatter, value, dateTimeSeparator) {
    return formatter
        .formatToParts(value)
        .map((part, index, parts) => {
        if (part.type === 'dayPeriod') {
            return part.value.toUpperCase().replace(/\./g, '');
        }
        if (dateTimeSeparator &&
            part.type === 'literal' &&
            parts[index + 1]?.type === 'hour') {
            return dateTimeSeparator;
        }
        return part.value;
    })
        .join('')
        .replace(/\u202f/g, ' ');
}
/**
 * Runs a formatter built from `intlDisplayOptions(format)`. Formats that render a
 * 12h clock go through `formatTimeWithNormalizedDayPeriod` so AM/PM is uniform across locales;
 * `'mediumDatetime'` also swaps the locale's date/time joiner for ` · `, the product's established look.
 *
 * Exported for use by sibling modules (e.g. timezone.ts) — not part of the public API surface.
 */
export function formatWithDisplayFormat(formatter, value, format) {
    if (format === 'mediumDatetime') {
        return formatTimeWithNormalizedDayPeriod(formatter, value, ' · ');
    }
    return format === 'time'
        ? formatTimeWithNormalizedDayPeriod(formatter, value)
        : formatter.format(value);
}
/**
 * Parses a manually-typed numeric date string (e.g. `"7/8/2026"`) into a Date
 * using the viewer's locale day/month order. Intended for use with
 * `onChangeRaw` in react-datepicker, which fires on every keystroke before
 * the full format is satisfied.
 *
 * Returns `null` for partial or non-matching input so in-progress keystrokes
 * are ignored by the caller.
 *
 * @example
 * parseUserTypedDate('7/8/2026', 'en-AU')  // → Date(2026, 7, 7)  [7 Aug — dd/MM]
 * parseUserTypedDate('7/8/2026', 'en-US')  // → Date(2026, 6, 8)  [Jul 8 — MM/dd]
 * parseUserTypedDate('7/8/26', 'en-US')    // → null  (4-digit year required)
 */
export function parseUserTypedDate(rawValue, locale) {
    const parts = rawValue?.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4,})$/);
    if (!parts) {
        return null;
    }
    const [, first, second, yearStr] = parts;
    const format = getDateFormat(locale, { type: 'unicode' });
    const isMonthFirst = format.indexOf('M') < format.indexOf('d');
    const day = parseInt(isMonthFirst ? second : first, 10);
    const month = parseInt(isMonthFirst ? first : second, 10);
    return new Date(parseInt(yearStr, 10), month - 1, day);
}
/**
 * Formats a date value per locale without timezone conversion.
 * Use for pure calendar day fields (event_date, due_date, birth_date) where
 * the value represents a day, not a UTC instant.
 *
 * @example
 * formatDateByLocale('2026-05-17', 'en-GB') // → "17/05/2026"
 * formatDateByLocale('2026-05-17', 'en-US') // → "05/17/2026"
 */
export function formatDateByLocale(value, locale, format = 'short') {
    const formatter = new Intl.DateTimeFormat(locale, intlDisplayOptions(format));
    return formatWithDisplayFormat(formatter, toDate(value), format);
}
//# sourceMappingURL=date.js.map